import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Divider,
  IconButton,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Stack,
  Snackbar,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Search,
  Person,
  Email,
  Phone,
  CheckCircle,
  Cancel,
  Schedule,
  Group,
  AdminPanelSettings,
  PersonRemove,
  PersonAdd,
  ContentCopy,
  Link as LinkIcon,
  PhotoCamera,
  CheckCircleOutline,
  CancelOutlined,
  FileDownload,
  DoneAll,
  MarkEmailRead,
  Send,
  DeleteOutline,
  GroupAdd,
} from '@mui/icons-material';
import { useAuth } from '@hooks/useAuth';
import { getVolunteersByOrganization, getOrgAdminsByOrganization, updateUserDocument, promoteToCoAdmin } from '@services/userService';
import { logActivity, LOG_TYPES } from '@services/activityLogService';
import { notifyVolunteerApproval } from '@services/notificationService';
import { getOrganizationById } from '@services/organizationService';
import { getPendingInvitesByOrg, revokeInvite } from '@services/inviteService';
import {
  createPreRegisteredVolunteer,
  getPreRegisteredVolunteersByOrg,
  deletePreRegisteredVolunteer,
} from '@services/preRegisteredVolunteerService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@services/firebase';

const STATUS_COLORS = {
  active: 'success',
  pending: 'warning',
  rejected: 'error',
};

const VolunteersPage = () => {
  const navigate = useNavigate();
  const { userData, isOrgAdmin, isOwner } = useAuth();

  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [actionTarget, setActionTarget] = useState(null); // { volunteer, type: 'approve' | 'reject' | 'remove' | 'promote' }
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  // Bulk selection for pending volunteers
  const [selectedPending, setSelectedPending] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState(0); // 0 = Members, 1 = Invited

  // Invite state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSnackbar, setInviteSnackbar] = useState('');
  const [orgInviteCode, setOrgInviteCode] = useState('');

  // Pending invites state
  const [pendingInvites, setPendingInvites] = useState([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeLoading, setRevokeLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  // Pre-registered volunteers (added directly by an org-admin, no account yet)
  const [preRegVolunteers, setPreRegVolunteers] = useState([]);
  const [preRegLoading, setPreRegLoading] = useState(false);
  const [addVolunteerDialogOpen, setAddVolunteerDialogOpen] = useState(false);
  const [addVolunteerForm, setAddVolunteerForm] = useState({ displayName: '', email: '', phone: '' });
  const [addingVolunteer, setAddingVolunteer] = useState(false);
  const [addVolunteerError, setAddVolunteerError] = useState('');
  const [removePreRegTarget, setRemovePreRegTarget] = useState(null);
  const [removingPreReg, setRemovingPreReg] = useState(false);

  const organizationId = userData?.organizationId;

  const loadVolunteers = useCallback(async () => {
    if (!organizationId) return;
    try {
      setLoading(true);
      const [volunteers, coAdmins, org] = await Promise.all([
        getVolunteersByOrganization(organizationId),
        getOrgAdminsByOrganization(organizationId),
        getOrganizationById(organizationId),
      ]);
      const data = [...coAdmins, ...volunteers];
      setVolunteers(data);
      if (org?.inviteCode) setOrgInviteCode(org.inviteCode);
    } catch (err) {
      setError('Failed to load volunteers. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  const loadInvites = useCallback(async () => {
    if (!organizationId) return;
    try {
      setInvitesLoading(true);
      const data = await getPendingInvitesByOrg(organizationId);
      // Sort newest first
      data.sort((a, b) => (b.invitedAt?.seconds || 0) - (a.invitedAt?.seconds || 0));
      setPendingInvites(data);
    } catch (err) {
      setError('Failed to load invites. Please refresh.');
    } finally {
      setInvitesLoading(false);
    }
  }, [organizationId]);

  const loadPreRegVolunteers = useCallback(async () => {
    if (!organizationId) return;
    try {
      setPreRegLoading(true);
      const data = await getPreRegisteredVolunteersByOrg(organizationId);
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setPreRegVolunteers(data);
    } catch (err) {
      setError('Failed to load pre-registered volunteers. Please refresh.');
    } finally {
      setPreRegLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (!isOrgAdmin()) {
      navigate('/dashboard');
      return;
    }
    loadVolunteers();
    loadInvites();
    loadPreRegVolunteers();
  }, [isOrgAdmin, loadVolunteers, loadInvites, loadPreRegVolunteers, navigate]);

  const handleConfirmAction = async () => {
    if (!actionTarget) return;
    const { volunteer, type } = actionTarget;
    try {
      setActionLoading(true);
      setActionError('');

      const logBase = {
        organizationId: userData.organizationId,
        organizationName: userData.organizationName,
        actorId: userData.uid,
        actorName: userData.displayName,
        targetId: volunteer.uid,
        targetName: volunteer.displayName,
      };

      if (type === 'remove') {
        await updateUserDocument(volunteer.uid, {
          organizationId: null,
          organizationName: null,
          status: 'active',
          ...(volunteer.userType === 'org-admin' && { userType: 'volunteer', role: null }),
        });
        logActivity({ ...logBase, type: LOG_TYPES.USER_REMOVED });
      } else if (type === 'promote') {
        await promoteToCoAdmin(volunteer.uid, userData.organizationId, userData.organizationName);
        logActivity({ ...logBase, type: LOG_TYPES.USER_PROMOTED });
      } else {
        const newStatus = type === 'approve' ? 'active' : 'rejected';
        await updateUserDocument(volunteer.uid, { status: newStatus });
        logActivity({
          ...logBase,
          type: type === 'approve' ? LOG_TYPES.USER_APPROVED : LOG_TYPES.USER_REJECTED,
        });
        try {
          await notifyVolunteerApproval(volunteer.uid, type === 'approve', volunteer.organizationName);
        } catch {
          // Non-fatal
        }
      }

      await loadVolunteers();
      setActionTarget(null);
    } catch (err) {
      setActionError('Failed to update volunteer status. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendInvites = async () => {
    const emails = inviteEmails
      .split(/[\n,]+/)
      .map((e) => e.trim())
      .filter((e) => e.includes('@'));
    if (emails.length === 0) {
      setInviteError('Please enter at least one valid email address.');
      return;
    }
    try {
      setInviting(true);
      setInviteError('');
      const fn = httpsCallable(functions, 'sendInviteEmail');
      const result = await fn({ emails, organizationId });
      setInviteDialogOpen(false);
      setInviteEmails('');
      setInviteSnackbar(`Invitation${result.data.sent !== 1 ? 's' : ''} sent to ${result.data.sent} address${result.data.sent !== 1 ? 'es' : ''}.`);
      loadInvites();
    } catch (err) {
      setInviteError(err?.message || 'Failed to send invitations. Please try again.');
    } finally {
      setInviting(false);
    }
  };

  const handlePhotoAction = async (volunteer, action) => {
    // action: 'approve' | 'reject'
    try {
      if (action === 'approve') {
        await updateUserDocument(volunteer.uid, { photoURL: volunteer.pendingPhotoURL, pendingPhotoURL: null });
        setInviteSnackbar(`Photo approved for ${volunteer.displayName}.`);
      } else {
        await updateUserDocument(volunteer.uid, { pendingPhotoURL: null });
        setInviteSnackbar(`Photo rejected for ${volunteer.displayName}.`);
      }
      await loadVolunteers();
    } catch (err) {
      setError('Failed to update photo. Please try again.');
    }
  };

  const handleExportCSV = () => {
    const rows = [
      ['Name', 'Email', 'Phone', 'Status', 'Role', 'Skills', 'Total Hours'],
      ...volunteers.map((v) => [
        v.displayName || '',
        v.email || '',
        v.phoneNumber || '',
        v.status || '',
        v.userType === 'org-admin' ? 'Co-Admin' : 'Volunteer',
        (v.skills || []).join('; '),
        v.totalHours || 0,
      ]),
    ];
    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${userData?.organizationName || 'volunteers'}-members.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkAction = async (action) => {
    if (selectedPending.size === 0) return;
    try {
      setBulkLoading(true);
      const newStatus = action === 'approve' ? 'active' : 'rejected';
      await Promise.all(
        Array.from(selectedPending).map((uid) => updateUserDocument(uid, { status: newStatus }))
      );
      setSelectedPending(new Set());
      setInviteSnackbar(`${selectedPending.size} volunteer${selectedPending.size !== 1 ? 's' : ''} ${action === 'approve' ? 'approved' : 'rejected'}.`);
      await loadVolunteers();
    } catch (err) {
      setError('Bulk action failed. Please try again.');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleRevokeInvite = async () => {
    if (!revokeTarget) return;
    try {
      setRevokeLoading(true);
      await revokeInvite(revokeTarget.id);
      setPendingInvites((prev) => prev.filter((i) => i.id !== revokeTarget.id));
      setInviteSnackbar(`Invite to ${revokeTarget.email} revoked.`);
      setRevokeTarget(null);
    } catch (err) {
      setError('Failed to revoke invite. Please try again.');
    } finally {
      setRevokeLoading(false);
    }
  };

  const handleResendInvite = async (invite) => {
    try {
      setResendingId(invite.id);
      const fn = httpsCallable(functions, 'sendInviteEmail');
      await fn({ emails: [invite.email], organizationId });
      setInviteSnackbar(`Invite resent to ${invite.email}.`);
      await loadInvites();
    } catch (err) {
      setError('Failed to resend invite. Please try again.');
    } finally {
      setResendingId(null);
    }
  };

  const handleAddVolunteer = async () => {
    const { displayName, email, phone } = addVolunteerForm;
    if (!displayName.trim() || !email.trim() || !email.includes('@')) {
      setAddVolunteerError('Please enter a name and a valid email address.');
      return;
    }
    try {
      setAddingVolunteer(true);
      setAddVolunteerError('');
      await createPreRegisteredVolunteer(userData.uid, {
        displayName: displayName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim() || null,
        organizationId,
        organizationName: userData.organizationName,
      });
      setAddVolunteerDialogOpen(false);
      setAddVolunteerForm({ displayName: '', email: '', phone: '' });
      setInviteSnackbar(`${displayName.trim()} added — they'll get email reminders once registered for an event.`);
      await loadPreRegVolunteers();
    } catch (err) {
      setAddVolunteerError('Failed to add volunteer. Please try again.');
    } finally {
      setAddingVolunteer(false);
    }
  };

  const handleRemovePreReg = async () => {
    if (!removePreRegTarget) return;
    try {
      setRemovingPreReg(true);
      await deletePreRegisteredVolunteer(removePreRegTarget.id);
      setPreRegVolunteers((prev) => prev.filter((v) => v.id !== removePreRegTarget.id));
      setInviteSnackbar(`Removed ${removePreRegTarget.displayName}.`);
      setRemovePreRegTarget(null);
    } catch (err) {
      setError('Failed to remove volunteer. Please try again.');
    } finally {
      setRemovingPreReg(false);
    }
  };

  const filtered = volunteers.filter((v) => {
    const matchesStatus = statusFilter === 'all' || v.status === statusFilter;
    const term = search.toLowerCase();
    const matchesSearch =
      !term ||
      v.displayName?.toLowerCase().includes(term) ||
      v.email?.toLowerCase().includes(term) ||
      v.phoneNumber?.includes(term) ||
      v.skills?.some((s) => s.toLowerCase().includes(term));
    return matchesStatus && matchesSearch;
  });

  const counts = {
    all: volunteers.length,
    active: volunteers.filter((v) => v.status === 'active').length,
    pending: volunteers.filter((v) => v.status === 'pending').length,
    rejected: volunteers.filter((v) => v.status === 'rejected').length,
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
          <Group color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" fontWeight="bold">
            Volunteers
          </Typography>
          <Chip label={volunteers.length} color="primary" size="small" sx={{ fontWeight: 'bold' }} />
          {pendingInvites.length > 0 && (
            <Chip
              icon={<MarkEmailRead sx={{ fontSize: '0.9rem !important' }} />}
              label={`${pendingInvites.length} invite${pendingInvites.length !== 1 ? 's' : ''} pending`}
              color="info"
              size="small"
            />
          )}
          {volunteers.filter((v) => v.pendingPhotoURL).length > 0 && (
            <Chip
              icon={<PhotoCamera sx={{ fontSize: '0.9rem !important' }} />}
              label={`${volunteers.filter((v) => v.pendingPhotoURL).length} photo${volunteers.filter((v) => v.pendingPhotoURL).length !== 1 ? 's' : ''} pending`}
              color="warning"
              size="small"
            />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary">
          {userData?.organizationName} — manage and review your organization's volunteers
        </Typography>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={`Members (${volunteers.length})`} />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              Invited
              {pendingInvites.length + preRegVolunteers.length > 0 && (
                <Chip label={pendingInvites.length + preRegVolunteers.length} size="small" color="info" sx={{ height: 18, fontSize: '0.7rem' }} />
              )}
            </Box>
          }
        />
      </Tabs>

      {/* ── Members Tab ── */}
      {activeTab === 0 && <>
      {/* Toolbar */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by name, email, phone, or skill…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />
          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(_, v) => { if (v) { setStatusFilter(v); setSelectedPending(new Set()); } }}
            size="small"
          >
            <ToggleButton value="all">All ({counts.all})</ToggleButton>
            <ToggleButton value="active">Active ({counts.active})</ToggleButton>
            <ToggleButton value="pending">Pending ({counts.pending})</ToggleButton>
            <ToggleButton value="rejected">Rejected ({counts.rejected})</ToggleButton>
          </ToggleButtonGroup>
          {statusFilter === 'pending' && counts.pending > 0 && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const pendingUids = filtered
                  .filter((v) => v.status === 'pending')
                  .map((v) => v.uid);
                if (selectedPending.size === pendingUids.length) {
                  setSelectedPending(new Set());
                } else {
                  setSelectedPending(new Set(pendingUids));
                }
              }}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {selectedPending.size === filtered.filter((v) => v.status === 'pending').length
                ? 'Deselect All'
                : 'Select All'}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => { setInviteDialogOpen(true); setInviteError(''); }}
            size="small"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Invite
          </Button>
          <Tooltip title="Add someone who won't sign up themselves — they'll get email reminders for events they're registered for">
            <Button
              variant="outlined"
              startIcon={<GroupAdd />}
              onClick={() => { setAddVolunteerDialogOpen(true); setAddVolunteerError(''); }}
              size="small"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Add Volunteer
            </Button>
          </Tooltip>
          <Tooltip title="Export all members as CSV">
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={handleExportCSV}
              size="small"
              disabled={volunteers.length === 0}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Export CSV
            </Button>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Bulk action bar — shown when pending volunteers are selected */}
      {selectedPending.size > 0 && (
        <Paper
          elevation={3}
          sx={{
            mb: 2,
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'primary.50',
            border: '1px solid',
            borderColor: 'primary.200',
          }}
        >
          <Typography variant="body2" fontWeight="medium" sx={{ flexGrow: 1 }}>
            {selectedPending.size} pending volunteer{selectedPending.size !== 1 ? 's' : ''} selected
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={bulkLoading ? <CircularProgress size={16} color="inherit" /> : <DoneAll />}
            onClick={() => handleBulkAction('approve')}
            disabled={bulkLoading}
          >
            Approve All
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            startIcon={<Cancel />}
            onClick={() => handleBulkAction('reject')}
            disabled={bulkLoading}
          >
            Reject All
          </Button>
          <Button size="small" onClick={() => setSelectedPending(new Set())} disabled={bulkLoading}>
            Clear
          </Button>
        </Paper>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Person sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {volunteers.length === 0 ? 'No volunteers yet' : 'No volunteers match your search'}
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ overflow: 'hidden' }}>
          {filtered.map((volunteer, idx) => (
            <Box key={volunteer.uid}>
              {idx > 0 && <Divider />}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  p: 2,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {/* Checkbox for pending volunteers */}
                {volunteer.status === 'pending' && (
                  <Checkbox
                    size="small"
                    checked={selectedPending.has(volunteer.uid)}
                    onChange={(e) => {
                      const next = new Set(selectedPending);
                      if (e.target.checked) next.add(volunteer.uid);
                      else next.delete(volunteer.uid);
                      setSelectedPending(next);
                    }}
                    sx={{ mt: 0.5, p: 0 }}
                  />
                )}

                {/* Avatar */}
                <Avatar
                  src={volunteer.photoURL}
                  sx={{
                    bgcolor:
                      volunteer.status === 'pending'
                        ? 'warning.main'
                        : volunteer.status === 'rejected'
                        ? 'error.main'
                        : 'primary.main',
                    flexShrink: 0,
                    mt: 0.5,
                  }}
                >
                  {volunteer.displayName?.charAt(0).toUpperCase() || <Person />}
                </Avatar>

                {/* Info */}
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {volunteer.displayName}
                    </Typography>
                    <Chip
                      label={volunteer.status}
                      size="small"
                      color={STATUS_COLORS[volunteer.status] || 'default'}
                    />
                    {volunteer.userType === 'org-admin' && (
                      <Chip
                        icon={<AdminPanelSettings sx={{ fontSize: 14 }} />}
                        label="Co-Admin"
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Email sx={{ fontSize: 15, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary">
                        {volunteer.email}
                      </Typography>
                    </Box>
                    {volunteer.phoneNumber && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Phone sx={{ fontSize: 15, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {volunteer.phoneNumber}
                        </Typography>
                      </Box>
                    )}
                    {volunteer.totalHours > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Schedule sx={{ fontSize: 15, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {volunteer.totalHours} hr{volunteer.totalHours !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {volunteer.skills?.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {volunteer.skills.map((skill, i) => (
                        <Chip key={i} label={skill} size="small" variant="outlined" />
                      ))}
                    </Box>
                  )}

                  {/* Pending photo review */}
                  {volunteer.pendingPhotoURL && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mt: 1,
                        p: 1,
                        bgcolor: 'warning.50',
                        border: '1px solid',
                        borderColor: 'warning.200',
                        borderRadius: 1,
                      }}
                    >
                      <PhotoCamera sx={{ fontSize: 16, color: 'warning.dark' }} />
                      <Typography variant="caption" color="warning.dark" fontWeight="medium">
                        New profile photo pending approval
                      </Typography>
                      <Avatar src={volunteer.pendingPhotoURL} sx={{ width: 36, height: 36 }} />
                      <Tooltip title="Approve photo">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handlePhotoAction(volunteer, 'approve')}
                        >
                          <CheckCircleOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject photo">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handlePhotoAction(volunteer, 'reject')}
                        >
                          <CancelOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                  {volunteer.status === 'pending' && (
                    <>
                      <Tooltip title="Approve">
                        <IconButton
                          color="success"
                          size="small"
                          onClick={() => setActionTarget({ volunteer, type: 'approve' })}
                        >
                          <CheckCircle />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reject">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => setActionTarget({ volunteer, type: 'reject' })}
                        >
                          <Cancel />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {isOwner() && volunteer.status === 'active' && volunteer.userType !== 'org-admin' && (
                    <Tooltip title="Promote to Co-Admin">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => setActionTarget({ volunteer, type: 'promote' })}
                      >
                        <AdminPanelSettings />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Remove from org">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => setActionTarget({ volunteer, type: 'remove' })}
                    >
                      <PersonRemove />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          ))}
        </Paper>
      )}
      {/* ── End Members Tab ── */}
      </>}

      {/* ── Invited Tab ── */}
      {activeTab === 1 && (
        <Box>
          {invitesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : pendingInvites.length === 0 && preRegVolunteers.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center' }}>
              <Send sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No pending invitations
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Send invitations using the Invite button, or add a volunteer directly, to see them tracked here.
              </Typography>
            </Paper>
          ) : (
            <Box>
            {pendingInvites.length > 0 && (
            <Paper elevation={2} sx={{ overflow: 'hidden', mb: preRegVolunteers.length > 0 ? 3 : 0 }}>
              {pendingInvites.map((invite, idx) => (
                <Box key={invite.id}>
                  {idx > 0 && <Divider />}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      p: 2,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Avatar sx={{ bgcolor: 'info.main', flexShrink: 0 }}>
                      <Email />
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight="medium" noWrap>
                        {invite.email}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Invited by {invite.invitedByName}
                        </Typography>
                        {invite.invitedAt?.seconds && (
                          <Typography variant="body2" color="text.secondary">
                            {new Date(invite.invitedAt.seconds * 1000).toLocaleDateString()}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <Tooltip title="Resend invite">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleResendInvite(invite)}
                            disabled={resendingId === invite.id}
                          >
                            {resendingId === invite.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Send fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Revoke invite">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setRevokeTarget(invite)}
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Paper>
            )}

            {preRegVolunteers.length > 0 && (
              <Paper elevation={2} sx={{ overflow: 'hidden' }}>
                {preRegVolunteers.map((volunteer, idx) => (
                  <Box key={volunteer.id}>
                    {idx > 0 && <Divider />}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Avatar sx={{ bgcolor: 'secondary.main', flexShrink: 0 }}>
                        <Person />
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" fontWeight="medium" noWrap>
                            {volunteer.displayName}
                          </Typography>
                          <Chip label="Added by admin" size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            {volunteer.email}
                          </Typography>
                          {volunteer.phone && (
                            <Typography variant="body2" color="text.secondary">
                              {volunteer.phone}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Email reminders only until they sign up to opt into SMS
                        </Typography>
                      </Box>
                      <Box sx={{ flexShrink: 0 }}>
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setRemovePreRegTarget(volunteer)}
                          >
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Paper>
            )}
            </Box>
          )}
        </Box>
      )}

      {/* Remove Pre-Registered Volunteer Confirm Dialog */}
      <Dialog
        open={Boolean(removePreRegTarget)}
        onClose={() => !removingPreReg && setRemovePreRegTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Volunteer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove <strong>{removePreRegTarget?.displayName}</strong>? Any event registrations tied to
            them will stop receiving reminders.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovePreRegTarget(null)} disabled={removingPreReg}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRemovePreReg}
            disabled={removingPreReg}
            startIcon={removingPreReg ? <CircularProgress size={18} /> : null}
          >
            {removingPreReg ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Volunteer Dialog */}
      <Dialog
        open={addVolunteerDialogOpen}
        onClose={() => !addingVolunteer && setAddVolunteerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Volunteer</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <DialogContentText sx={{ mb: 2 }}>
            For volunteers who won't sign up themselves. They'll show up on event rosters and get
            email reminders for events they're registered for. They can't receive text reminders or
            push notifications until they create an account and opt in themselves.
          </DialogContentText>
          {addVolunteerError && <Alert severity="error" sx={{ mb: 2 }}>{addVolunteerError}</Alert>}
          <Stack spacing={2}>
            <TextField
              label="Name"
              fullWidth
              value={addVolunteerForm.displayName}
              onChange={(e) => setAddVolunteerForm((f) => ({ ...f, displayName: e.target.value }))}
              disabled={addingVolunteer}
              autoFocus
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={addVolunteerForm.email}
              onChange={(e) => setAddVolunteerForm((f) => ({ ...f, email: e.target.value }))}
              disabled={addingVolunteer}
            />
            <TextField
              label="Phone (optional)"
              fullWidth
              value={addVolunteerForm.phone}
              onChange={(e) => setAddVolunteerForm((f) => ({ ...f, phone: e.target.value }))}
              disabled={addingVolunteer}
              helperText="For your records only — SMS stays off until they opt in themselves"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddVolunteerDialogOpen(false)} disabled={addingVolunteer}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddVolunteer}
            disabled={addingVolunteer}
            startIcon={addingVolunteer ? <CircularProgress size={18} /> : null}
          >
            {addingVolunteer ? 'Adding…' : 'Add Volunteer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Confirm Dialog */}
      <Dialog
        open={Boolean(revokeTarget)}
        onClose={() => !revokeLoading && setRevokeTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Revoke Invitation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Revoke the invitation for <strong>{revokeTarget?.email}</strong>? They will no longer be
            able to use this invite link to join your organization.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)} disabled={revokeLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRevokeInvite}
            disabled={revokeLoading}
            startIcon={revokeLoading ? <CircularProgress size={18} /> : null}
          >
            {revokeLoading ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => !inviting && setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Volunteers</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {/* Section 1: Invite Code */}
          {orgInviteCode && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Share Invite Code
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Volunteers enter this code on the registration page to join your organization.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  value={orgInviteCode}
                  size="small"
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontWeight: 'bold', letterSpacing: 2 } }}
                  sx={{ flexGrow: 1 }}
                />
                <Tooltip title="Copy code">
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(orgInviteCode);
                      setInviteSnackbar('Invite code copied!');
                    }}
                  >
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* Section 2: Registration Link */}
          {orgInviteCode && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Share Registration Link
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Send this link directly — the invite code will be pre-filled on registration.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  value={`https://servesync.me/register?invite=${orgInviteCode}`}
                  size="small"
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                  sx={{ flexGrow: 1 }}
                />
                <Tooltip title="Copy link">
                  <IconButton
                    onClick={() => {
                      navigator.clipboard.writeText(`https://servesync.me/register?invite=${orgInviteCode}`);
                      setInviteSnackbar('Registration link copied!');
                    }}
                  >
                    <LinkIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* Section 3: Email Invitations */}
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Send Email Invitations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Each person will receive a branded invitation email with the registration link.
            </Typography>
            <TextField
              label="Email addresses"
              placeholder="jane@example.com, john@example.com"
              fullWidth
              multiline
              minRows={3}
              value={inviteEmails}
              onChange={(e) => setInviteEmails(e.target.value)}
              helperText="Separate multiple addresses with commas or new lines"
              disabled={inviting}
            />
            {inviteError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {inviteError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteDialogOpen(false)} disabled={inviting}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSendInvites}
            disabled={inviting}
            startIcon={inviting ? <CircularProgress size={18} /> : <Email />}
          >
            {inviting ? 'Sending…' : 'Send Invitations'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <Dialog
        open={Boolean(actionTarget)}
        onClose={() => !actionLoading && setActionTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionTarget?.type === 'approve'
            ? 'Approve Volunteer'
            : actionTarget?.type === 'reject'
            ? 'Reject Volunteer'
            : actionTarget?.type === 'promote'
            ? 'Promote to Co-Admin'
            : 'Remove from Organization'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {actionTarget?.type === 'approve' ? (
              <>
                Approve <strong>{actionTarget?.volunteer?.displayName}</strong>? They will gain
                access to your organization's events and activities.
              </>
            ) : actionTarget?.type === 'reject' ? (
              <>
                Reject <strong>{actionTarget?.volunteer?.displayName}</strong>? They will not be
                able to access your organization.
              </>
            ) : actionTarget?.type === 'promote' ? (
              <>
                Promote <strong>{actionTarget?.volunteer?.displayName}</strong> to co-admin? They
                will immediately gain full org-admin access — creating events, managing
                volunteers, and editing events.
              </>
            ) : (
              <>
                Remove <strong>{actionTarget?.volunteer?.displayName}</strong> from your
                organization? They will lose access to all events and their membership will be
                cleared.
              </>
            )}
          </DialogContentText>
          {actionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {actionError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionTarget(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={actionTarget?.type === 'approve' || actionTarget?.type === 'promote' ? 'success' : 'error'}
            onClick={handleConfirmAction}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={18} /> : null}
          >
            {actionLoading
              ? 'Processing…'
              : actionTarget?.type === 'approve'
              ? 'Approve'
              : actionTarget?.type === 'reject'
              ? 'Reject'
              : actionTarget?.type === 'promote'
              ? 'Promote'
              : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(inviteSnackbar)}
        autoHideDuration={4000}
        onClose={() => setInviteSnackbar('')}
        message={inviteSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Container>
  );
};

export default VolunteersPage;
